import { useState, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { QRCodeCanvas } from "qrcode.react";
import { X, GripVertical, Trash2, AlertTriangle, CheckCircle, Printer, Send } from "lucide-react";
import { publishMenu } from "../../../../lib/api/restaurant.apis";
import assets from "../../../../assets/assets";

interface MenuImage {
  id: string;
  url: string;
}

interface MenuBuilderModalProps {
  restaurantId: string;
  restaurantName: string;
  galleryMenuImages: { id: string; url: string }[];
  publishedPdfUrl: string | null; // current backend PDF
  onClose: () => void;
  onPublishSuccess: () => void;
}

// ─── Sortable Image Card ──────────────────────────────────────────────────────

function SortableCard({
  item,
  onRemove,
}: {
  item: MenuImage;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm"
    >
      <img
        src={item.url}
        alt="menu"
        className="w-full h-28 object-cover"
      />

      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 bg-black/50 rounded p-0.5 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="w-4 h-4 text-white" />
      </button>

      {/* Remove button (PDF only) */}
      <button
        onClick={() => onRemove(item.id)}
        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function MenuBuilderModal({
  restaurantId,
  restaurantName,
  galleryMenuImages,
  publishedPdfUrl,
  onClose,
  onPublishSuccess,
}: MenuBuilderModalProps) {
  const stableMenuUrl = `${window.location.origin}/menu/${restaurantId}`;

  // Local ordered selection (starts from all gallery menu images)
  const [orderedImages, setOrderedImages] = useState<MenuImage[]>(
    galleryMenuImages.map((img) => ({ id: img.id, url: img.url }))
  );

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [published, setPublished] = useState(false);

  const qrRef = useRef<HTMLDivElement>(null);

  // Detect unpublished changes: simple check — if published PDF exists,
  // compare current ordered URLs to what's already published (backend-driven).
  // We approximate: "has changed" = user reordered or removed something after load.
  const [initialSnapshot] = useState(() =>
    galleryMenuImages.map((img) => img.url).join(",")
  );
  const currentSnapshot = orderedImages.map((img) => img.url).join(",");
  const hasUnpublishedChanges = currentSnapshot !== initialSnapshot || !publishedPdfUrl;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedImages((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  function handleRemove(id: string) {
    setOrderedImages((prev) => prev.filter((img) => img.id !== id));
  }

  async function handlePublish() {
    if (orderedImages.length === 0) return;
    setPublishing(true);
    setPublishError(null);

    try {
      const res = await publishMenu(restaurantId, orderedImages.map((img) => img.url));

      if (!res.success) throw new Error("Failed to publish menu");

      setPublished(true);
      onPublishSuccess();
    } catch (err: any) {
      setPublishError(err.message || "Something went wrong.");
    } finally {
      setPublishing(false);
    }
  }

  function handlePrintQR() {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const canvas = qrRef.current?.querySelector("canvas");
    const qrDataUrl = canvas?.toDataURL("image/png") ?? "";
    const logoUrl = assets.logo_icon;
    const siteUrl = "https://gemio.app";

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Menu QR Poster – ${restaurantName}</title>
            <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #fff; }
            .poster { text-align: center; padding: 48px 40px; border: 2px solid #eee; border-radius: 16px; max-width: 400px; width: 100%; }
            .header { display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
            .logo { width: 30px; height: 30px; object-fit: contain; border-radius: 8px; }
            .site-link { font-family: 'Logo-sans', sans-serif; font-weight: 600; font-size: 24px; color: #ee5219; margin-left: 8px; text-decoration: none; }
            .name { font-size: 26px; font-weight: 700; color: #111; margin-bottom: 8px; }
            .divider { width: 60px; height: 3px; background: #FFD166; margin: 12px auto; border-radius: 2px; }
            .qr { margin: 24px auto; display: block; }
            .tagline { font-size: 15px; color: #666; margin-top: 12px; letter-spacing: 0.03em; }
            .url { font-size: 11px; color: #aaa; margin-top: 8px; word-break: break-all; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
            </style>
        </head>
        <body>
            <div class="poster">
            <div class="header">
                <img src="${logoUrl}" class="logo" alt="logo" />
                <a href="${siteUrl}" class="site-link">Gemio</a>
            </div>
            <div class="name">${restaurantName}</div>
            <div class="divider"></div>
            <img src="${qrDataUrl}" class="qr" width="200" height="200" alt="QR Code" />
            <div class="tagline">📱 Scan to View Our Menu</div>
            <div class="url">${stableMenuUrl}</div>
            </div>
            <script>window.onload = () => { window.print(); }</script>
        </body>
        </html>
    `);

    printWindow.document.close();
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Menu Builder & QR Code</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          {/* Unpublished changes badge */}
          {hasUnpublishedChanges && !published && (
            <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>You have unpublished changes. Publish to update the QR menu.</span>
            </div>
          )}

          {published && (
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 text-sm">
              <CheckCircle className="w-4 h-4 shrink-0" />
              <span>Menu published successfully! QR code is now live.</span>
            </div>
          )}

          {/* Drag-and-drop grid */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Menu Pages <span className="text-gray-400 font-normal">({orderedImages.length} pages)</span>
            </p>
            <p className="text-xs text-gray-400 mb-3">
              Drag to reorder. Remove pages from the PDF without deleting them from your gallery.
            </p>

            {orderedImages.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
                No menu images selected. Add menu photos in the Gallery tab first.
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedImages.map((i) => i.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {orderedImages.map((item) => (
                      <SortableCard key={item.id} item={item} onRemove={handleRemove} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* QR Code section */}
          {(published || publishedPdfUrl) && (
            <div className="border border-gray-100 rounded-xl p-5 bg-gray-50 flex flex-col sm:flex-row items-center gap-6">
              <div ref={qrRef} className="shrink-0">
                <QRCodeCanvas
                  value={stableMenuUrl}
                  size={160}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  level="H"
                  includeMargin
                />
              </div>
              <div className="text-center sm:text-left">
                <p className="text-sm font-semibold text-gray-800 mb-1">Your Menu QR Code</p>
                <p className="text-xs text-gray-500 mb-3">
                  This QR always points to the same URL — it never changes even when you update the menu.
                </p>
                <code className="text-xs bg-white border border-gray-200 rounded px-2 py-1 text-gray-600 break-all block mb-4">
                  {stableMenuUrl}
                </code>
                <button
                  onClick={handlePrintQR}
                  className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print QR Poster
                </button>
              </div>
            </div>
          )}

          {publishError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              {publishError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePublish}
            disabled={publishing || orderedImages.length === 0}
            className="flex items-center gap-2 bg-[#FFD166] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {publishing ? "Publishing..." : "Publish Menu"}
          </button>
        </div>
      </div>
    </div>
  );
}